import { Injectable } from '@angular/core';
import { BaseHttpApiService } from './base-http.service';
import { Observable } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { DateFormatHelperService } from './date-format-helper.service';
import { Assets } from '../Dtos/assets-enum';
import { AdHocInstructionsGridDTO } from '../Dtos/ad-hoc/instructions-grid-dto';
import { AdHocInstructionDTO } from '../Dtos/ad-hoc/adhoc-instruction-dto';
import { AdHocInstructionRetailDTO } from '../Dtos/ad-hoc/adhoc-instruction-retail-dto';

@Injectable({
  providedIn: 'root'
})
export class AdHocInstructionsService extends BaseHttpApiService {
  constructor(private httpClient: HttpClient,
    private dateHelper: DateFormatHelperService) {
    super();
  }

  GetActiveAndFutureInstructions(asset: Assets): Observable<AdHocInstructionsGridDTO[]> {
    let controller: string = this.GetController(asset);
    if (controller == "")
      throw ("Invalid Asset");

    let url: string = `${this.apiURL}${controller}/AdHoc`;
    if (controller == "AdHoc")
      url += "/?asset=" + asset;

    return this.httpClient.get<AdHocInstructionsGridDTO[]>(url);
  }

  GetInstructionById(instructionId:number|string): Observable<AdHocInstructionDTO>{
    return this.httpClient.get<AdHocInstructionDTO>(`${this.apiURL}AdHoc/AdHoc/${instructionId}`)
  }

  GetInstructionsByDateRange(asset: Assets, from: Date, to: Date): Observable<AdHocInstructionsGridDTO[]> {
    from.setHours(0, 0, 0, 0); // Set time to 00:00
    to.setHours(23, 59, 59, 999); // Set time to 23:59
  
    let controller: string = this.GetController(asset);
    if (controller == "")
      throw ("Invalid Asset");

    let url: string = `${this.apiURL}${controller}/AdHoc/${this.dateHelper.GetAPIFormat(from)}/${this.dateHelper.GetAPIFormat(to)}`;
    if (controller == "AdHoc")
      url += "/?asset=" + asset;

    return this.httpClient.get<AdHocInstructionsGridDTO[]>(url);
  }

  SaveInstruction(asset: Assets, instruction: AdHocInstructionDTO) {
    // If it's retail default the units to "none" as the instructions are not unit specific
    if (asset == Assets.Retail)
      instruction.units = "none";

    if (typeof instruction.issuedFrom === 'string') {
      instruction.issuedFrom = new Date(instruction.issuedFrom);
    }
    instruction.issuedFrom.setUTCHours(0, 0, 0, 0); // Set time to 00:00 GMT
    if (instruction.issuedTo) {
      if (typeof instruction.issuedTo === 'string') {
        instruction.issuedTo = new Date(instruction.issuedTo);
      }
      instruction.issuedTo.setUTCHours(23, 59, 59, 999);
    } // Set time to 23:59

    let url: string = `${this.apiURL}AdHoc/AdHoc/?asset=${asset}`;
    return this.httpClient.post<object>(url, instruction);
  }

  SaveInstructionsWithAttachments(asset: Assets, instruction: AdHocInstructionDTO, files: File[]) {
    const formData = new FormData();
    
    // If it's retail default the units to "none" as the instructions are not unit specific
    if (asset == Assets.Retail)
      instruction.units = "none";
    
    // Append data properties to FormData
    if (typeof instruction.issuedFrom === 'string') {
      instruction.issuedFrom = new Date(instruction.issuedFrom);
    }
    instruction.issuedFrom.setUTCHours(0, 0, 0, 0); // Set time to 00:00 GMT
    // Append data properties to FormData
    formData.append('IssuedFrom', instruction.issuedFrom.toISOString());

    if ((instruction.issuedTo ?? "") != "") {
      if (typeof instruction.issuedTo === 'string') {
        instruction.issuedTo = new Date(instruction.issuedTo);
      }
      // Set time to 23:59
      instruction.issuedTo?.setUTCHours(23, 59, 59, 999);
      formData.append('IssuedTo', instruction.issuedTo?.toISOString() ?? "");
    } 
    else
      formData.append('IssuedTo', instruction.issuedTo?.toString() ?? "");

    formData.append('DateComment', instruction.dateComment);
    formData.append('Subject', instruction.subject);
    formData.append('Details', instruction.details);
    formData.append('Units', instruction.units);
    formData.append('files', JSON.stringify(instruction.files));
    // Append files to FormData
    for (let i = 0; i < files.length; i++) {
      formData.append('uploadedFiles', files[i], files[i].name);
    }

    // Send the request
    let url: string = `${this.apiURL}AdHoc/adhocWithFiles/?asset=${asset}`;
    return this.httpClient.post<object>(url, formData);
  }

  ChangeStatus(instructionId:number|string, status: string){
    return this.httpClient.put<object>(`${this.apiURL}AdHoc/AdHoc/SetStatus?instructionId=${instructionId}&status=${status}`,'') 
  }

  GetRetailInstructionById(instructionId:number|string): Observable<AdHocInstructionRetailDTO>{
    return this.httpClient.get<AdHocInstructionRetailDTO>(`${this.apiURL}AdHoc/AdHoc/Retail/${instructionId}`)
  }

  SaveRetailInstruction(instruction: AdHocInstructionRetailDTO) {
    instruction.units = "none";
    let url: string = `${this.apiURL}AdHoc/Retail`;
    if (typeof instruction.issuedFrom === 'string') {
      instruction.issuedFrom = new Date(instruction.issuedFrom);
    }
    instruction.issuedFrom.setUTCHours(0, 0, 0, 0); // Set time to 00:00 GMT
    if (instruction.issuedTo) {
      if (typeof instruction.issuedTo === 'string') {
        instruction.issuedTo = new Date(instruction.issuedTo);
      }
      instruction.issuedTo.setUTCHours(23, 59, 59, 999);
    } // Set time to 23:59
  
    return this.httpClient.post<object>(url, instruction);
  }

  SaveRetailInstructionsWithAttachments(instruction: AdHocInstructionRetailDTO, files: File[]) {
    const formData = new FormData();

    if (typeof instruction.issuedFrom === 'string') {
      instruction.issuedFrom = new Date(instruction.issuedFrom);
    }
    instruction.issuedFrom.setUTCHours(0, 0, 0, 0); // Set time to 00:00 GMT
    // Append data properties to FormData
    formData.append('IssuedFrom', instruction.issuedFrom.toISOString());

    if ((instruction.issuedTo ?? "") != "") {
      if (typeof instruction.issuedTo === 'string') {
        instruction.issuedTo = new Date(instruction.issuedTo);
      }
      // Set time to 23:59
      instruction.issuedTo?.setUTCHours(23, 59, 59, 999);
      formData.append('IssuedTo', instruction.issuedTo?.toISOString() ?? "");
    } 
    else
      formData.append('IssuedTo', instruction.issuedTo?.toString() ?? "");


    // If it's retail default the units to "none" as the instructions are not unit specific
    instruction.units = "none";
    
    formData.append('DateComment', instruction.dateComment);
    formData.append('Subject', instruction.subject);
    formData.append('Details', instruction.details);
    formData.append('Units', instruction.units);
    formData.append('files', JSON.stringify(instruction.files));
    // Append files to FormData
    for (let i = 0; i < files.length; i++) {
      formData.append('uploadedFiles', files[i], files[i].name);
    }
    // Append forecasts
    formData.append('forecasts', JSON.stringify(instruction.forecasts));


    // Send the request
    let url: string = `${this.apiURL}AdHoc/RetailWithFiles`;
    return this.httpClient.post<object>(url, formData);
  }

  private GetController(asset: Assets): string {
    return asset == Assets.MorayEast ? "MorayEast" :
    asset == Assets.MorayWest ? "MorayWest" : "AdHoc";
  }

  DownloadFile(fileId:number): Observable<HttpResponse<Blob>>{
    return this.httpClient.get<Blob>(`${this.apiURL}AdHoc/File/${fileId}`, {observe: 'response', responseType: 'blob' as 'json'}) 
  }
}